/**
 * Exposes window.__computeDashPrepActive(storeId) — a Promise<boolean> that
 * replicates the TaskAlertDashboard prep-card eligibility check exactly, using
 * the same helpers (getTemplateNextDate, getTemplateKind) and the same data
 * sources (count_sessions templates + stores period config + count_sessions
 * non-template rows for the next 3 days).
 *
 * Attaching to window lets the vanilla-JS sidebar and mobile-nav call this from
 * any page that loads application.js without needing the dashboard to be open.
 */

import supabase from '../config/supabase';
import { getTemplateNextDate } from './prepCardHelpers';

function _toDateStr(d) {
  return d.getFullYear() + '-'
    + String(d.getMonth() + 1).padStart(2, '0') + '-'
    + String(d.getDate()).padStart(2, '0');
}

async function computeDashPrepActive(storeId) {
  if (!storeId) return false;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const in3Days = new Date(today);
    in3Days.setDate(today.getDate() + 3);
    const todayStr  = _toDateStr(today);
    const in3DStr   = _toDateStr(in3Days);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    // Fetch same data set as TaskAlertDashboard.loadPrep():
    //   a) Templates (count_sessions where is_template=true) with their schedule
    //   b) Store's inventory_period_config (needed for EOP templates without own schedule)
    //   c) Non-template sessions already created for the window (to check "no session")
    const [tmplRes, periodRes, sessRes] = await Promise.all([
      supabase
        .from('count_sessions')
        .select('id,name,order_calculations_enabled,ordering_cycle_config,updated_at')
        .eq('store_id', storeId)
        .eq('is_template', true)
        .not('status', 'in', '(cancelled,deleted)'),
      supabase
        .from('stores')
        .select('inventory_period_config')
        .eq('id', storeId)
        .single(),
      supabase
        .from('count_sessions')
        .select('id,template_id,session_date,status')
        .eq('store_id', storeId)
        .eq('is_template', false)
        .gte('session_date', todayStr)
        .lte('session_date', in3DStr),
    ]);

    const templates  = tmplRes.data  || [];
    const periodCfg  = periodRes.data?.inventory_period_config ?? null;
    const sessions   = sessRes.data  || [];

    // Mirror TaskAlertDashboard's qualifying-card loop exactly.
    for (const t of templates) {
      const nextDate = getTemplateNextDate(t, periodCfg);
      if (!nextDate || nextDate < todayStr || nextDate > in3DStr) continue;
      const hasSession = sessions.some(
        (s) => String(s.template_id) === String(t.id) &&
          s.session_date === nextDate &&
          !['cancelled', 'deleted'].includes((s.status || '').toLowerCase())
      );
      if (!hasSession) return true;
    }
    return false;
  } catch (_) {
    return false;
  }
}

window.__computeDashPrepActive = computeDashPrepActive;
